/**
 * Unit/Box Toggle Component
 * Allows switching between buying products by unit or by box
 * Works with masmusculo.unidades_por_caja metafield
 */

if (!customElements.get('unit-box-toggle')) {
    class UnitBoxToggle extends HTMLElement {
        constructor() {
            super();
            this.currentMode = 'unit';
            this.unitsPerBox = 0;
            this.currentPrice = 0;
            this.variantBoxData = {};
            this.quantityInputs = [];
            this.sectionId = this.dataset.sectionId;
        }

        connectedCallback() {
            // Parse variant box data
            this.parseVariantBoxData();

            // Get both desktop and mobile quantity inputs
            this.findQuantityInputs();

            // Set up button click handlers
            this.setupButtonHandlers();

            // Listen for variant changes
            this.setupVariantChangeListener();

            // Initialize with current variant
            this.updateForCurrentVariant();
        }

        parseVariantBoxData() {
            const dataScript = this.querySelector('script[data-variant-box-data]');
            if (dataScript) {
                try {
                    this.variantBoxData = JSON.parse(dataScript.textContent);
                } catch (e) {
                    console.error('Error parsing variant box data:', e);
                }
            }
        }

        findQuantityInputs() {
            // Find desktop quantity input
            const desktopInput = document.querySelector(`#Quantity-${this.sectionId}`);
            if (desktopInput) {
                this.quantityInputs.push(desktopInput);
            }

            // Find mobile quantity input
            const mobileInput = document.querySelector(`#Quantity-Mobile-${this.sectionId}`);
            if (mobileInput) {
                this.quantityInputs.push(mobileInput);
            }

            // Fallback: try to find any quantity input in the section
            if (this.quantityInputs.length === 0) {
                const allInputs = document.querySelectorAll('input[name="quantity"]');
                allInputs.forEach(input => {
                    if (input.id && input.id.includes(this.sectionId)) {
                        this.quantityInputs.push(input);
                    }
                });
            }
        }

        setupButtonHandlers() {
            const buttons = this.querySelectorAll('.unit-box-toggle__button');
            buttons.forEach(button => {
                button.addEventListener('click', (e) => {
                    e.preventDefault();
                    const mode = button.dataset.mode;
                    this.switchMode(mode);
                });
            });
        }

        setupVariantChangeListener() {
            // variantChange fires after the fetch resolves and carries the full variant object
            if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
                    const { variant, sectionId } = event.data || {};
                    if (sectionId !== this.sectionId) return;
                    if (variant) this.updateForVariant(variant.id);
                });
            }

            // Fallback: custom event dispatched by product-info on the same publish cycle
            document.addEventListener('variant:change', (event) => {
                const { variant, sectionId } = event.detail || {};
                if (sectionId !== this.sectionId) return;
                if (variant) this.updateForVariant(variant.id);
            });
        }

        getCurrentVariantId() {
            // Try to get current variant from the selected variant script
            const variantSelects = document.querySelector('variant-selects');
            if (variantSelects) {
                const selectedVariantScript = variantSelects.querySelector('script[data-selected-variant]');
                if (selectedVariantScript) {
                    try {
                        const variantData = JSON.parse(selectedVariantScript.textContent);
                        return variantData.id;
                    } catch (e) {
                        console.error('Error parsing selected variant:', e);
                    }
                }
            }

            // Fallback: try to get from product-info element
            const productInfo = document.querySelector('product-info');
            if (productInfo) {
                const forms = productInfo.querySelectorAll('form[action*="/cart/add"]');
                for (const form of forms) {
                    const variantIdInput = form.querySelector('input[name="id"]');
                    if (variantIdInput && variantIdInput.value) {
                        return parseInt(variantIdInput.value);
                    }
                }
            }

            return null;
        }

        updateForCurrentVariant() {
            const currentVariantId = this.getCurrentVariantId();
            if (!currentVariantId) { this.hide(); return; }
            this.updateForVariant(currentVariantId);
        }

        updateForVariant(variantId) {
            const data = this.variantBoxData[variantId] || {};
            this.unitsPerBox = parseInt(data.unitsPerBox || 0);
            this.currentPrice = parseInt(data.price || 0);

            if (this.unitsPerBox > 0) {
                this.show();
                this.updateBoxButtonText();
                this.updateButtonPrices();
                this.switchMode('unit', false);
            } else {
                this.hide();
            }
        }

        updateBoxButtonText() {
            const boxButton = this.querySelector('.unit-box-toggle__button--box');
            const unitsCountSpan = boxButton.querySelector('.unit-box-toggle__units-count');
            if (unitsCountSpan) {
                unitsCountSpan.textContent = this.unitsPerBox;
            }
        }

        updateButtonPrices() {
            const unitPriceSpan = this.querySelector('.unit-box-toggle__button--unit .unit-box-toggle__button-price');
            const boxPriceSpan = this.querySelector('.unit-box-toggle__button--box .unit-box-toggle__button-price');

            if (unitPriceSpan) {
                unitPriceSpan.textContent = ` (${this.formatMoney(this.currentPrice)})`;
            }
            if (boxPriceSpan) {
                boxPriceSpan.textContent = ` (${this.formatMoney(this.currentPrice * this.unitsPerBox)})`;
            }
        }

        formatMoney(cents) {
            return new Intl.NumberFormat('es-ES', {
                style: 'currency',
                currency: 'EUR',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(cents / 100);
        }

        switchMode(mode, updateQuantity = true) {
            if (mode === this.currentMode) return;

            this.currentMode = mode;

            // Update button states
            const buttons = this.querySelectorAll('.unit-box-toggle__button');
            buttons.forEach(button => {
                const isActive = button.dataset.mode === mode;
                button.classList.toggle('active', isActive);
                button.setAttribute('aria-pressed', isActive.toString());
            });

            // Update ALL quantity inputs (desktop and mobile)
            if (updateQuantity && this.quantityInputs.length > 0) {
                const newValue = mode === 'box'
                    ? this.unitsPerBox
                    : (parseInt(this.quantityInputs[0].getAttribute('data-min')) || 1);

                this.quantityInputs.forEach(input => {
                    input.value = newValue;
                    // Trigger change event to update any dependent components
                    input.dispatchEvent(new Event('change', { bubbles: true }));
                });
            }
        }

        show() {
            this.classList.add('active');
        }

        hide() {
            this.classList.remove('active');
            // Reset to unit mode when hiding
            this.switchMode('unit', false);
        }
    }

    customElements.define('unit-box-toggle', UnitBoxToggle);
}